import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { AdminInvestorListItem } from '@workspace/api-backend'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellPrimary, CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import { useState } from 'react'
import { useAdminInvestorsQuery } from '@/hooks/api'
import { CellLink } from '@/utils/data-table'
import { formatFullDate } from '@/utils/format'

type SearchParams = {
  page?: number
  search?: string
}

export const Route = createFileRoute('/_protected/admin/investors/')({
  component: AdminInvestorsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

const columns: TableColumn<AdminInvestorListItem>[] = [
  {
    header: 'ID',
    width: '100px',
    render: (investor) => (
      <CellLink
        to="/admin/investors/$id"
        params={{ id: investor.id }}
        className="font-mono text-xs"
      >
        {investor.id.slice(0, 8)}
      </CellLink>
    ),
  },
  {
    header: 'Name',
    render: (investor) => <CellPrimary>{investor.name}</CellPrimary>,
  },
  {
    header: 'Owner',
    render: (investor) =>
      investor.owner ? (
        <CellLink to="/admin/users/$id" params={{ id: investor.owner.id }}>
          {investor.owner.name || investor.owner.email}
        </CellLink>
      ) : (
        <CellText>-</CellText>
      ),
  },
  {
    header: 'Slug',
    width: '200px',
    render: (investor) => <CellText>{investor.slug ?? '-'}</CellText>,
  },
  {
    header: 'Credits',
    width: '90px',
    render: (investor) => <CellText>{investor.creditBalance}</CellText>,
  },
  {
    header: 'Created',
    width: '130px',
    render: (investor) => <CellText>{formatFullDate(investor.createdAt)}</CellText>,
  },
]

function AdminInvestorsPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')

  const investorsQuery = useAdminInvestorsQuery({
    query: { page, limit: LIMIT, search: search || undefined },
  })

  const investors = investorsQuery.data?.data?.items ?? []
  const total = investorsQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-6 py-6">
      <SearchInput
        placeholder="Search investors..."
        value={searchInput}
        onChange={setSearchInput}
        onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
      />
      <DataTable
        columns={columns}
        data={investors}
        isLoading={investorsQuery.isLoading}
        emptyMessage="No investors found."
        keyExtractor={(i) => i.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
